/**
 * ErrorClassifier - Utility for classifying and categorizing WASM/typesetter errors
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for identifying different types of errors
 * that can occur during typesetting operations, enabling appropriate error handling.
 */

/**
 * Check if an error is a WASM-related error (memory access, unreachable, C++ exception)
 * 
 * These errors indicate serious WASM engine issues that may require worker reinitialization.
 * 
 * @param error - Error object or error message string
 * @returns True if the error is a WASM-related error
 */
export function isWasmError(error: Error | string | unknown): boolean {
  const message = typeof error === 'string' 
    ? error 
    : error instanceof Error 
      ? error.message 
      : String(error);
  
  if (typeof message !== 'string') {
    return false;
  }
  
  // Check for WASM-specific error patterns
  return /memory access out of bounds|unreachable|C\+\+ exception/i.test(message);
}

/**
 * Check if an error is a soft-handleable error (can be safely ignored or handled gracefully)
 * 
 * These errors indicate benign conditions that don't require full error propagation,
 * such as paragraph not found (which can happen during incremental edits).
 * 
 * @param error - Error object or error message string
 * @returns True if the error can be soft-handled
 */
export function isSoftHandleableError(error: Error | string | unknown): boolean {
  const message = typeof error === 'string' 
    ? error 
    : error instanceof Error 
      ? error.message 
      : String(error);
  
  if (typeof message !== 'string') {
    return false;
  }
  
  // Check for soft-handleable error patterns
  return /^Paragraph not found: /.test(message) 
    || /^WASM error: No paragraph found at position /.test(message) 
    || /memory access out of bounds/i.test(message);
}

/**
 * Classify an error into categories
 * 
 * Returns an object indicating the error type and recommended handling strategy.
 * 
 * @param error - Error object or error message string
 * @returns Error classification with type and handling recommendations
 */
export function classifyError(error: Error | string | unknown): {
  isWasmError: boolean;
  isSoftHandleable: boolean;
  requiresReinit: boolean;
  message: string;
} {
  const message = typeof error === 'string' 
    ? error 
    : error instanceof Error 
      ? error.message 
      : String(error);
  
  const wasmError = isWasmError(message);
  const softHandleable = isSoftHandleableError(message);
  
  return {
    isWasmError: wasmError,
    isSoftHandleable: softHandleable,
    requiresReinit: wasmError && !softHandleable, // WASM errors that aren't soft-handleable need reinit
    message: typeof message === 'string' ? message : String(message)
  };
}

